import React, { useEffect, useState } from 'react';
/**
 * PriceDisplay — simulates Shopify Markets currency behavior.
 *
 * It detects the visitor's browser locale via `navigator.language` and picks
 * the appropriate currency symbol ($, €, £). The numeric amount is kept as the
 * product's base value and formatted with the Argentine convention (dot as the
 * thousands separator), matching the source copy (e.g. $29.999).
 *
 * NOTE: This is a front-end simulation of Markets — it swaps the symbol based
 * on locale; it does not perform live FX conversion.
 */
type CurrencyMeta = {
  symbol: string;
  code: string;
};
function resolveCurrency(locale: string): CurrencyMeta {
  const lower = (locale || '').toLowerCase();
  // Pound — United Kingdom / Ireland English
  if (lower === 'en-gb' || lower === 'en-ie' || lower.startsWith('cy')) {
    return {
      symbol: '£',
      code: 'GBP'
    };
  }
  // Euro — common Eurozone locales
  const euroPrefixes = ['de', 'fr', 'es-es', 'it', 'nl', 'pt-pt', 'ga', 'el', 'fi', 'et', 'lv', 'lt', 'sk', 'sl', 'mt', 'eu', 'ca'];
  if (euroPrefixes.some(p => lower === p || lower.startsWith(p + '-') || lower === p.split('-')[0])) {
    // Guard: Spanish-from-Spain -> Euro, but Latin American Spanish stays on $.
    if (lower.startsWith('es') && lower !== 'es-es') {
      return {
        symbol: '$',
        code: 'ARS'
      };
    }
    return {
      symbol: '€',
      code: 'EUR'
    };
  }
  // Default — dollar (covers Latin American Spanish, US English, etc.)
  return {
    symbol: '$',
    code: 'ARS'
  };
}
function useCurrency(): CurrencyMeta {
  const [currency, setCurrency] = useState<CurrencyMeta>({
    symbol: '$',
    code: 'ARS'
  });
  useEffect(() => {
    const locale = typeof navigator !== 'undefined' ? navigator.language || navigator.languages && navigator.languages[0] || 'es-AR' : 'es-AR';
    setCurrency(resolveCurrency(locale));
  }, []);
  return currency;
}
function formatAmount(amount: number): string {
  // Argentine format: thousands separated by dots (e.g. 29999 -> 29.999).
  return new Intl.NumberFormat('es-AR').format(amount);
}
interface PriceDisplayProps {
  amount: number;
  className?: string;
  /** When true, renders as a struck-through "old" price. */
  strike?: boolean;
  /** Optional smaller currency symbol styling. */
  symbolClassName?: string;
}
export function PriceDisplay({
  amount,
  className = '',
  strike = false,
  symbolClassName = ''
}: PriceDisplayProps) {
  const {
    symbol,
    code
  } = useCurrency();
  return <span className={`${strike ? 'line-through opacity-60' : ''} ${className}`} aria-label={`${symbol}${formatAmount(amount)} ${code}`} data-id="element-132">
      <span className={symbolClassName} aria-hidden="true" data-id="element-133">
        {symbol}
      </span>
      <span aria-hidden="true" data-id="element-134">{formatAmount(amount)}</span>
    </span>;
}